import styled from 'styled-components'
import React, { useMemo, } from 'react'
import { useParams, } from 'react-router-dom'
import { Chat as ChatIcon, } from '@mui/icons-material'

import { AdminRelatedLink, } from '@components'

import { CHANNEL_TALK_CHANNEL_ID, } from '@globals'

import { Agent, } from '@types'

interface Props {
  agents: Array<Agent>
  className?: string
}

const Container = styled.div`
  padding: 0;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(244, 247, 251, 0.96) 100%);

  .header {
    padding: 16px 18px 14px;
    background: linear-gradient(135deg, rgb(35, 47, 62) 0%, rgb(51, 65, 85) 100%);
    color: white;
  }

  h3 {
    font-weight: 800;
    font-size: 19px;
    letter-spacing: -0.02em;
    line-height: 1.35;

    a {
      color: rgba(255, 255, 255, 0.82);
    }
  }

  .header-subtitle {
    margin-top: 8px;
    color: rgba(255, 255, 255, 0.76);
    font-size: 13px;
  }

  .body {
    padding: 16px 18px 14px;
  }

  .info-grid {
    display: grid;
    gap: 10px;
  }

  .info-card {
    padding: 12px 13px;
    border-radius: 12px;
    background-color: rgba(255, 255, 255, 0.86);
    border: 1px solid rgba(226, 232, 240, 0.95);
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.04);
  }

  .card-label {
    display: block;
    margin-bottom: 5px;
    color: rgb(100, 116, 139);
    font-size: 12px;
    font-weight: 700;
    letter-spacing: -0.01em;
  }

  .card-value {
    color: rgb(51, 65, 85);
    line-height: 1.5;
    font-size: 14px;
    word-break: break-word;
  }

  .currently-working-requesting-title {
    color: rgb(51, 65, 85);
    font-weight: 800;
    font-size: 14px;
    margin-top: 18px;
    margin-bottom: 10px;
  }

  .currently-working-requesting-info {
    padding: 12px 13px;
    border-radius: 14px;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.92) 0%, rgba(240, 245, 250, 0.86) 100%);
    border: 1px solid rgba(203, 213, 225, 0.9);

    p {
      color: rgb(71, 85, 105);
      line-height: 1.45;
      font-size: 14px;
      margin-top: 6px;

      &.without-margin {
        margin-top: 0;
      }
    }
  }

  .footer {
    margin-top: 14px;
    justify-content: flex-end;

    a {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 42px;
      height: 42px;
      border-radius: 12px;
      color: rgb(67, 81, 249);
      background-color: rgba(67, 81, 249, 0.08);
      border: 1px solid rgba(67, 81, 249, 0.14);
      cursor: pointer;
    }
  }
`

function AgentDetail(props: Props) {
  const { agents, className, } = props
  const { agentId, } = useParams()

  const selectedAgent = useMemo(
    () => {
      if(!agentId) {
        return undefined
      }

      return agents.find(agent => agent.id.toString() === agentId)
    },
    [ agents, agentId, ]
  )

  const onChatClick = () => {
    if(selectedAgent) {
      const channelTalkSearchQuery = `{"and":[{"or":[{"key":"user.memberId","type":"string","operator":"$in","values":["${ selectedAgent.id }"]}]}]}`
      const encodedSearchQuery = btoa(channelTalkSearchQuery)

      window.open(
        `https://desk.channel.io/#/channels/${ CHANNEL_TALK_CHANNEL_ID }/contacts?query=${ encodedSearchQuery }&search=`
      )
    }
  }

  if(!selectedAgent) {
    return null
  }

  return (
    <Container className={ className }>
      <div className='header'>
        <h3>
          { selectedAgent.name } 평카인&nbsp;
          (
            <AdminRelatedLink
              linkType='agent'
              id={ selectedAgent.id }>
              { selectedAgent.agent_profile.id }
            </AdminRelatedLink>
          )
        </h3>
        <p className='header-subtitle'>
          { selectedAgent.agent_profile.level } 레벨 평가사 정보
        </p>
      </div>
      <div className='body'>
        <div className='info-grid'>
          <div className='info-card'>
            <span className='card-label'>연락처</span>
            <div className='card-value'>
              <AdminRelatedLink linkType='phoneCall' id={ selectedAgent.mobile_number }>{ selectedAgent.mobile_number }</AdminRelatedLink>
            </div>
          </div>
          <div className='info-card'>
            <span className='card-label'>희망 도착지</span>
            <div className='card-value'>
              { selectedAgent.agent_location.desired_destination_address || '없음' }
            </div>
          </div>
        </div>
        <p className='currently-working-requesting-title'>
          현재 수행중인 오더 정보
        </p>
        <div className='currently-working-requesting-info'>
          {
            !selectedAgent.currently_working_requesting ? (
              <p className='without-margin'>수행중인 오더 없음</p>
            ) : (
              <>
                <p className='without-margin'>
                  오더 번호:&nbsp;
                  <AdminRelatedLink
                    linkType='requesting'
                    id={ selectedAgent.currently_working_requesting.id }>
                    { selectedAgent.currently_working_requesting.id }
                  </AdminRelatedLink>
                </p>
                <p>출발지: { selectedAgent.currently_working_requesting.source_address  }</p>
                <p>도착지: { selectedAgent.currently_working_requesting.destination_address  }</p>
              </>
            )
          }
        </div>
        <div className='footer row'>
          <a onClick={ onChatClick }>
            <ChatIcon />
          </a>
        </div>
      </div>
    </Container>
  )
}

export default styled(AgentDetail)``
